import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { LEAD_STATUSES, STATUS_COLORS, stageAge } from "@/lib/constants";
import { ShieldCheck, GripVertical, Clock } from "lucide-react";

const ageCls = (d) => (d >= 7 ? "bg-rose-100 text-rose-700" : d >= 3 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500");
const FINAL = ["Sale done", "Closed", "Denied", "Disqualified"];

const Card = ({ lead, teamName, onOpen }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.lead_id, data: { lead } });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;
  const age = stageAge(lead);
  const stuck = age !== null && age >= 7 && !FINAL.includes(lead.status);
  return (
    <div ref={setNodeRef} style={style} data-testid="board-card"
      className={`bg-white rounded-xl border p-3 shadow-sm transition-shadow ${stuck ? "border-rose-300" : "border-slate-200"} ${isDragging ? "shadow-xl opacity-90" : "hover:shadow-md"}`}>
      <div className="flex items-start gap-2">
        <button {...listeners} {...attributes} className="text-slate-300 cursor-grab active:cursor-grabbing touch-none mt-0.5" aria-label="Drag" data-testid="board-card-handle"><GripVertical size={16} /></button>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={onOpen}>
          <p className="text-sm font-semibold text-[#081226] truncate flex items-center gap-1">{lead.name}{lead.ekyc_verified && <ShieldCheck size={13} className="text-emerald-500 shrink-0" />}</p>
          <p className="font-mono-num text-[11px] text-[#B8860B]">{lead.visitor_id}</p>
          <p className="text-[11px] text-slate-400 truncate">{lead.interests?.join(", ") || lead.purpose}</p>
          <div className="flex items-center justify-between gap-2 mt-1">
            <p className="text-[11px] font-semibold text-slate-500 truncate">{teamName(lead.assigned_to)}</p>
            {age !== null && !FINAL.includes(lead.status) && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1 shrink-0 ${ageCls(age)}`} data-testid="board-card-stage-age" title="Days in current stage">
                <Clock size={10} /> {age === 0 ? "Today" : `${age}d`}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Column = ({ stage, leads, teamName, onOpen }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const stuck = FINAL.includes(stage) ? 0 : leads.filter((l) => (stageAge(l) ?? 0) >= 7).length;
  return (
    <div ref={setNodeRef} data-testid={`board-column-${stage.toLowerCase().replace(/\s+/g, "-")}`}
      className={`w-[230px] shrink-0 rounded-2xl p-2.5 flex flex-col transition-colors ${isOver ? "bg-orange-50 ring-2 ring-[#E8752A]" : "bg-slate-100/70"}`}>
      <div className="flex items-center justify-between px-1 mb-2">
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[stage]}`}>{stage}</span>
        <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">{stuck > 0 && <span className="text-[10px] text-rose-600" data-testid="board-column-stuck">{stuck} stuck</span>}{leads.length}</span>
      </div>
      <div className="space-y-2 min-h-[80px] max-h-[62vh] overflow-y-auto">
        {leads.map((l) => <Card key={l.lead_id} lead={l} teamName={teamName} onOpen={() => onOpen(l)} />)}
      </div>
    </div>
  );
};

export const LeadBoard = ({ leads, teamName, onMove, onOpen }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }));
  const onDragEnd = ({ active, over }) => {
    const lead = active.data.current?.lead;
    if (lead && over && over.id !== lead.status) onMove(lead, over.id);
  };
  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd} autoScroll={{ acceleration: 2, threshold: { x: 0.08, y: 0 } }}>
      <p className="text-xs text-slate-400 mb-2">Drag a card by its handle to change the stage. The clock shows days in the current stage (amber 3+ days, red 7+ days = stuck). Scroll sideways to see all 13 stages →</p>
      <div className="flex gap-3 overflow-x-auto pb-3 board-scroll" data-testid="lead-board">
        {LEAD_STATUSES.map((s) => <Column key={s} stage={s} leads={leads.filter((l) => l.status === s)} teamName={teamName} onOpen={onOpen} />)}
      </div>
    </DndContext>
  );
};
